import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, CalendarClock, CheckCircle2, Download, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  listCompanies,
  listResolutionTypes,
  multiCompanyBatch,
  downloadMultiZip,
} from "@/lib/api";
import Combobox from "@/components/Combobox";
import SpecificFieldsPanel, { coerceSpecificFieldNumbers } from "@/components/SpecificFieldsPanel";
import { isoToDmy, dmyToIso } from "@/lib/date";

// Default resolution types pre-selected for a quarterly board meeting.
// `disclosure_interest` (MBP-1) — directors disclose their interest at the
// first board meeting of every FY and whenever there is a change; safe to
// repeat quarterly as a "noting" item.
// `approve_quarterly_results` — adoption of the quarterly accounts.
const DEFAULT_PRESET = ["disclosure_interest", "approve_quarterly_results"];

// Stable client-side IDs for the item rows so React's reconciler doesn't get
// confused when the user adds / removes rows out of order. Not a security uid.
let _itemUidCounter = 0;
const nextItemUid = () => `it-${++_itemUidCounter}-${Date.now()}`;

const MAX_CO = 10;
const MAX_ITEMS = 5;

// FY = April-March. Quarter ends: 30-Jun (Q1), 30-Sep (Q2), 31-Dec (Q3), 31-Mar (Q4).
// Suggested meeting date for a quarter = 30 days after quarter-end.
function quartersForFY(fyStartYear) {
  return [
    { id: "Q1", label: "Q1 (Apr-Jun)", end: `${fyStartYear}-06-30`, suggested: `${fyStartYear}-07-30` },
    { id: "Q2", label: "Q2 (Jul-Sep)", end: `${fyStartYear}-09-30`, suggested: `${fyStartYear}-10-30` },
    { id: "Q3", label: "Q3 (Oct-Dec)", end: `${fyStartYear}-12-31`, suggested: `${fyStartYear + 1}-01-30` },
    { id: "Q4", label: "Q4 (Jan-Mar)", end: `${fyStartYear + 1}-03-31`, suggested: `${fyStartYear + 1}-04-30` },
  ];
}

function currentFY() {
  const now = new Date();
  // FY starts April. Jan-Mar → previous calendar year is the start.
  const startYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  return { startYear, label: `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}` };
}

function currentQuarterForFY(startYear) {
  const now = new Date();
  const m = now.getMonth(); // 0-11
  // FY months: Apr=3 (Q1), Jul=6 (Q2), Oct=9 (Q3), Jan(next)=0 (Q4)
  if (now.getFullYear() === startYear) {
    if (m >= 3 && m <= 5) return "Q1";
    if (m >= 6 && m <= 8) return "Q2";
    if (m >= 9 && m <= 11) return "Q3";
  }
  return "Q4";
}

export default function QuarterlyPack() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fy = useMemo(() => currentFY(), []);
  const quarters = useMemo(() => quartersForFY(fy.startYear), [fy.startYear]);
  const [activeQuarter, setActiveQuarter] = useState(() => currentQuarterForFY(fy.startYear));
  const activeQ = useMemo(() => quarters.find((q) => q.id === activeQuarter), [quarters, activeQuarter]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [items, setItems] = useState(
    DEFAULT_PRESET.map((k) => ({ uid: nextItemUid(), resolution_type_key: k, specific_fields: {} })),
  );
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime] = useState("11:00 AM");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    if (activeQ) setMeetingDate(activeQ.suggested);
  }, [activeQ]);

  useEffect(() => {
    Promise.all([listCompanies(), listResolutionTypes()])
      .then(([cRes, tRes]) => {
        setCompanies(cRes.data || []);
        setTypes(tRes.data || []);
        // Default selection: ALL companies (capped to MAX_CO).
        const ids = (cRes.data || []).slice(0, MAX_CO).map((c) => c.id);
        setSelectedIds(ids);
      })
      .catch(() => toast.error("Could not load companies / resolution types"))
      .finally(() => setLoading(false));
  }, []);

  const typeOptions = useMemo(() => {
    const groups = {};
    for (const t of types) {
      const cat = t.category || "Other";
      (groups[cat] = groups[cat] || []).push(t);
    }
    return Object.entries(groups).flatMap(([cat, list]) =>
      list.map((t) => ({ value: t.key, label: t.title, group: cat })),
    );
  }, [types]);

  const typeByKey = useMemo(() => {
    const out = {};
    for (const t of types) out[t.key] = t;
    return out;
  }, [types]);

  const allResolutionIds = useMemo(
    () =>
      (result?.results || [])
        .flatMap((r) => (r.resolutions || []).map((x) => x.id))
        .filter(Boolean),
    [result],
  );

  const canRun =
    !running &&
    selectedIds.length > 0 &&
    items.length > 0 &&
    items.every((it) => it.resolution_type_key) &&
    meetingDate;

  // Members of first selected company — for SpecificFieldsPanel dropdown.
  const firstSelectedMembers = useMemo(() => {
    const firstId = selectedIds[0];
    if (!firstId) return [];
    const c = companies.find((x) => x.id === firstId);
    return c?.members || [];
  }, [selectedIds, companies]);

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    try {
      const res = await multiCompanyBatch({
        company_ids: selectedIds,
        items: items.map((it) => {
          const rt = typeByKey[it.resolution_type_key];
          const sf = coerceSpecificFieldNumbers(rt?.specific_fields, it.specific_fields);
          return {
            resolution_type_key: it.resolution_type_key,
            is_special_resolution: false,
            include_shareholder_resolution: false,
            specific_fields: sf && Object.keys(sf || {}).length > 0 ? sf : undefined,
          };
        }),
        meeting_date: meetingDate,
        meeting_date_overrides: {},
        meeting_time: meetingTime,
        meeting_place: "",  // backend falls back to each company's registered office
        meeting_type: "board",
        include_notice: true,
        include_minutes: true,
        use_style_library: true,
      });
      setResult(res.data);
      toast.success(
        `Quarterly pack: ${res.data.total_resolutions} drafts saved across ${res.data.succeeded} companies`,
      );
      // Auto-trigger the bundle ZIP after a quick beat
      const ids = (res.data.results || [])
        .flatMap((r) => (r.resolutions || []).map((x) => x.id))
        .filter(Boolean);
      if (ids.length) {
        setDownloadingZip(true);
        try {
          await downloadMultiZip(ids, {
            includeNotice: true,
            includeMinutes: true,
            formats: ["pdf", "docx"],
          });
          toast.success("Quarterly pack ZIP downloaded");
        } catch {
          toast.error("Could not auto-download ZIP — use the button below.");
        } finally {
          setDownloadingZip(false);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally {
      setRunning(false);
    }
  };

  const handleDownloadZip = async () => {
    if (!allResolutionIds.length) return;
    setDownloadingZip(true);
    try {
      await downloadMultiZip(allResolutionIds, {
        includeNotice: true,
        includeMinutes: true,
        formats: ["pdf", "docx"],
      });
      toast.success("ZIP downloaded");
    } catch {
      toast.error("ZIP download failed");
    } finally { setDownloadingZip(false); }
  };

  const toggleCompany = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_CO) {
        toast.warning(`Max ${MAX_CO} companies per quarterly pack`);
        return prev;
      }
      return [...prev, id];
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-10 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading quarterly pack…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="quarterly-pack-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700 mb-2">
          <CalendarClock className="w-3.5 h-3.5" /> Quarterly Pack · FY {fy.label}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          One click. One quarter. Every company.
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          We'll draft MBP-1 disclosures + quarterly-results adoption (plus anything you add) for the selected companies, save each meeting separately to the vault, and download a single ZIP containing every PDF + DOCX, organised per company.
        </p>
      </div>

      {/* Quarter selector */}
      <section className="bg-white border border-slate-200 p-5 mb-5" data-testid="quarter-selector">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {quarters.map((q) => {
            const isActive = q.id === activeQuarter;
            return (
              <button
                key={q.id}
                onClick={() => setActiveQuarter(q.id)}
                className={`px-4 py-3 border text-left transition ${
                  isActive ? "border-blue-700 bg-blue-50/60" : "border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`quarter-${q.id}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{q.id}</div>
                <div className="text-sm font-medium text-slate-900 mt-0.5">{q.label}</div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Ends {isoToDmy(q.end)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-600 uppercase tracking-wide">Meeting Date (default for all)</label>
            <input
              value={isoToDmy(meetingDate)}
              onChange={(e) => setMeetingDate(e.target.value)}
              onBlur={(e) => {
                const iso = dmyToIso(e.target.value);
                if (iso || !e.target.value) setMeetingDate(iso || "");
              }}
              placeholder="DD-MM-YYYY"
              inputMode="numeric"
              className="mt-1 w-full px-3 py-2 border border-slate-300 text-sm"
              data-testid="quarterly-meeting-date"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Suggested: 30 days after quarter-end. Override per company on the result screen if needed.
            </p>
          </div>
          <div className="text-sm text-slate-600 self-end">
            <span className="text-xs uppercase tracking-wider text-slate-500">Resolution set</span>
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-800 text-[11px]">MBP-1 Disclosure of Interest</span>
              <span className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-800 text-[11px]">Adoption of Quarterly Results</span>
              <span className="text-[11px] text-slate-500">·</span>
              <span className="text-[11px] text-slate-500">+ anything else you add below</span>
            </div>
          </div>
        </div>
      </section>

      {/* Companies + resolutions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Companies */}
        <section className="bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-700" /> Companies
            </h2>
            <div className="text-xs text-slate-500">
              {selectedIds.length} / {Math.min(MAX_CO, companies.length)} selected
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto border border-slate-200">
            {companies.length === 0 && (
              <div className="text-sm text-slate-500 p-4 text-center">
                No companies yet. <Link to="/companies/new" className="text-blue-700 underline">Add one</Link>.
              </div>
            )}
            {companies.map((c) => {
              const checked = selectedIds.includes(c.id);
              return (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 p-3 border-b border-slate-100 cursor-pointer transition last:border-b-0 ${
                    checked ? "bg-blue-50/50" : "hover:bg-slate-50"
                  }`}
                  data-testid={`quarterly-co-row-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCompany(c.id)}
                    className="w-4 h-4 accent-blue-700"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.company_type || c.type} · {c.cin || "—"} · {(c.directors || []).length} directors
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        {/* Resolutions */}
        <section className="bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-700" /> Resolutions for Each Company
            </h2>
            <div className="text-xs text-slate-500">{items.length} / {MAX_ITEMS}</div>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => {
              const rt = typeByKey[it.resolution_type_key];
              return (
              <div key={it.uid} className="flex flex-col gap-2" data-testid={`quarterly-item-${idx}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <Combobox
                      options={typeOptions}
                      value={it.resolution_type_key}
                      onChange={(v) =>
                        setItems(items.map((x) => (x.uid === it.uid
                          ? { ...x, resolution_type_key: v, specific_fields: {} }
                          : x)))
                      }
                      placeholder="Pick a resolution type…"
                      testId={`quarterly-item-${idx}-type`}
                    />
                  </div>
                  {items.length > 1 && (
                    <button
                      onClick={() => setItems(items.filter((x) => x.uid !== it.uid))}
                      className="text-xs text-slate-400 hover:text-red-600 p-2"
                      data-testid={`quarterly-item-${idx}-remove`}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {it.resolution_type_key && rt?.specific_fields?.length > 0 && (
                  <SpecificFieldsPanel
                    fields={rt.specific_fields}
                    values={it.specific_fields}
                    onChange={(sf) =>
                      setItems(items.map((x) => (x.uid === it.uid ? { ...x, specific_fields: sf } : x)))
                    }
                    title={rt.title}
                    testIdPrefix={`quarterly-specific-${idx}`}
                    members={firstSelectedMembers}
                  />
                )}
              </div>
              );
            })}
            {items.length < MAX_ITEMS && (
              <button
                onClick={() =>
                  setItems([...items, { uid: nextItemUid(), resolution_type_key: "", specific_fields: {} }])
                }
                className="w-full mt-1 py-2 border border-dashed border-slate-300 text-sm text-slate-500 hover:border-blue-700 hover:text-blue-700"
                data-testid="quarterly-add-item"
              >
                + Add another resolution
              </button>
            )}
          </div>
        </section>
      </div>

      {/* Action bar */}
      {!result && (
        <div className="sticky bottom-4 z-10 mt-5 bg-white border border-slate-200 p-4 shadow-lg flex items-center justify-between">
          <div className="text-sm text-slate-700">
            <span className="font-semibold">
              {selectedIds.length} companies × {items.filter((i) => i.resolution_type_key).length} resolutions
            </span>
            {selectedIds.length > 0 && items.some((i) => i.resolution_type_key) && (
              <span className="text-slate-500">
                {" "}= {selectedIds.length * items.filter((i) => i.resolution_type_key).length} drafts, then auto-bundled ZIP.
              </span>
            )}
          </div>
          <button
            disabled={!canRun}
            onClick={handleRun}
            className="px-5 py-2.5 bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="quarterly-run-cta"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Drafting…
              </>
            ) : (
              <>
                <CalendarClock className="w-4 h-4" /> Run Quarterly Pack
              </>
            )}
          </button>
        </div>
      )}

      {/* Results */}
      {result && (
        <section className="bg-white border border-slate-200 p-6 mt-5" data-testid="quarterly-results">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Quarterly pack ready
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                {result.total_resolutions} resolutions saved across {result.succeeded} companies.
                {result.failed > 0 && (
                  <span className="text-red-700"> {result.failed} failed.</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {allResolutionIds.length > 0 && (
                <button
                  onClick={handleDownloadZip}
                  disabled={downloadingZip}
                  className="px-3 py-2 bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2"
                  data-testid="quarterly-download-zip"
                >
                  {downloadingZip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Re-download ZIP
                </button>
              )}
              <button
                onClick={() => navigate("/vault")}
                className="px-3 py-2 border border-slate-300 text-sm hover:bg-slate-50"
                data-testid="quarterly-open-vault"
              >
                Open Vault
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {result.results.map((r) => (
              <div
                key={r.company_id}
                className={`border p-4 ${r.error ? "border-red-200 bg-red-50/40" : "border-slate-200"}`}
                data-testid={`quarterly-result-${r.company_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-slate-900">{r.company_name}</div>
                    <div className="text-xs text-slate-500">Meeting on {isoToDmy(r.meeting_date)}</div>
                  </div>
                  {r.meeting_id && (
                    <Link to={`/meetings/${r.meeting_id}`} className="text-xs text-blue-700 hover:underline">
                      View meeting →
                    </Link>
                  )}
                </div>
                {r.error ? (
                  <div className="text-sm text-red-700">{r.error}</div>
                ) : (
                  <ul className="space-y-1">
                    {r.resolutions.map((res) => (
                      <li key={res.id} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">
                          <span className="font-mono text-xs text-slate-500 mr-2">{res.resolution_number}</span>
                          {res.resolution_title}
                        </span>
                        <Link to={`/resolutions/${res.id}`} className="text-xs text-blue-700 hover:underline">
                          Open
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
